/**
 * The one product vocabulary the dashboard speaks.
 *
 * Every value here was read out of the committed application, not invented:
 *
 *  - frame shapes      lib/widgets/listing/shape_filter_bar.dart (_shapeOrder)
 *                      and assets/shape_filters/drawn/01..13-*.png
 *  - audiences         lib/widgets/listing/category_tabs.dart
 *  - glasses types     lib/widgets/listing/type_tabs.dart
 *  - size options      lib/widgets/listing/size_filter_bar.dart (_SizeSegments)
 *
 * Product family is NOT a backend column. The backend carries one free-form
 * `category` string, and Flutter's type tabs filter on exactly that string.
 * Family is therefore derived from it rather than stored twice — a second
 * stored taxonomy could drift out of step with the one Flutter actually reads.
 */

export const PRODUCT_FAMILIES = ["glasses", "contact_lenses", "accessories"] as const;
export type ProductFamily = (typeof PRODUCT_FAMILIES)[number];

export const FAMILY_LABELS: Record<ProductFamily, string> = {
  glasses: "Glasses",
  contact_lenses: "Contact Lenses",
  accessories: "Accessories",
};

/** Audience applies to Glasses only. Flutter's category tabs also carry an
 *  "all" tab, but that is a filter position, not a value a product can hold. */
export const AUDIENCES = ["men", "women", "kids"] as const;
export type Audience = (typeof AUDIENCES)[number];

export const AUDIENCE_LABELS: Record<Audience, string> = {
  men: "Men",
  women: "Women",
  kids: "Kids",
};

export const GLASSES_TYPES = ["sunglasses", "eyeglasses"] as const;
export type GlassesType = (typeof GLASSES_TYPES)[number];

export const GLASSES_TYPE_LABELS: Record<GlassesType, string> = {
  sunglasses: "Sunglasses",
  eyeglasses: "Eyeglasses",
};

/**
 * The thirteen frame shapes the application ships, in the application's own
 * order. A glasses product holds exactly one of these.
 *
 * The count is asserted in the test suite: an accidental fourteenth entry here
 * would offer the administrator a shape the phone cannot filter by, and a
 * missing entry would make a real shape unreachable.
 */
export const FRAME_SHAPES = [
  "square",
  "cat-eye",
  "round",
  "rectangle",
  "aviator",
  "browline",
  "geometric",
  "oval",
  "heart",
  "wrap-around",
  "full-rim",
  "half-rim",
  "rimless",
] as const;
export type FrameShape = (typeof FRAME_SHAPES)[number];

/** Title-case for display. The stored value is always the lowercase key. */
export function frameShapeLabel(shape: FrameShape | string): string {
  return String(shape)
    .split("-")
    .map((part) => (part ? part[0]!.toUpperCase() + part.slice(1) : part))
    .join("-");
}

/**
 * Size options exactly as the application stores and filters them.
 *
 * The phone's size segments read "S", "M", "L" — those are the stored values.
 * The dashboard shows the long word so a shop administrator is not asked to
 * decode an abbreviation, but what gets saved is the short key.
 */
export const SIZE_OPTIONS = ["S", "M", "L"] as const;
export type SizeOption = (typeof SIZE_OPTIONS)[number];

export const SIZE_LABELS: Record<SizeOption, string> = {
  S: "Small",
  M: "Medium",
  L: "Large",
};

export function isSizeOption(value: string): value is SizeOption {
  return (SIZE_OPTIONS as readonly string[]).includes(value);
}

/**
 * The stored `category` for a classification.
 *
 * Glasses split into the two type values Flutter's type tabs already use;
 * the other two families are categories in their own right.
 */
export function categoryFor(
  family: ProductFamily,
  glassesType?: GlassesType | null,
): string | null {
  if (family === "glasses") return glassesType ?? null;
  return family;
}

/**
 * The reverse: which family a stored category belongs to.
 *
 * Returns null for a category the application does not know — an older product
 * carrying something unexpected is shown as unclassified rather than filed
 * under a family it may not belong to.
 */
export function familyForCategory(category: string | null): ProductFamily | null {
  if (!category) return null;
  const key = category.trim().toLowerCase();
  if (key === "sunglasses" || key === "eyeglasses") return "glasses";
  if (key === "contact_lenses") return "contact_lenses";
  if (key === "accessories") return "accessories";
  return null;
}

/** The glasses type carried by a stored category, when it is a glasses one. */
export function glassesTypeForCategory(category: string | null): GlassesType | null {
  const key = (category ?? "").trim().toLowerCase();
  return key === "sunglasses" || key === "eyeglasses" ? key : null;
}

/**
 * Every stored `category` a family covers, in a fixed order.
 *
 * Glasses covers TWO, and that is the fact the rest of the dashboard has to
 * work around rather than wish away: the admin list endpoint matches category
 * with an equality test (`Product.category == category` in
 * product_service.py), so there is no single value that asks it for glasses.
 * A screen that wants one family's products asks this which categories to
 * request and requests each of them.
 *
 * This is the inverse of [familyForCategory] and the two are checked against
 * each other in the tests: a category reachable from here that does not resolve
 * back to the same family would put a product under a tab that cannot find it.
 */
export function categoriesForFamily(family: ProductFamily): string[] {
  return family === "glasses" ? [...GLASSES_TYPES] : [family];
}

/** Whether an arbitrary string — a URL parameter, say — names a family. */
export function isProductFamily(value: string | null): value is ProductFamily {
  return (PRODUCT_FAMILIES as readonly string[]).includes(value ?? "");
}

export function isFrameShape(value: string): value is FrameShape {
  return (FRAME_SHAPES as readonly string[]).includes(value);
}

export function isAudience(value: string): value is Audience {
  return (AUDIENCES as readonly string[]).includes(value);
}

export function isGlassesType(value: string): value is GlassesType {
  return (GLASSES_TYPES as readonly string[]).includes(value);
}
